import type { Task } from "@app/core";
import { priorityToneClass } from "../task-colors.js";
import { washStyle } from "../block-wash.js";
import { Check, cn, Repeat } from "@app/ui";
import { useState, type CSSProperties, type MouseEvent } from "react";
import { beginBlockDrag, endBlockDrag, setDragTaskId, setTaskDragging } from "./drag.js";
import { TaskContextMenu } from "./TaskContextMenu.js";
import { dayStart, spanDays } from "./month-helpers.js";
import { FaintOverlay } from "./FaintOverlay.js";
import { useFlashHighlight } from "./useFlashHighlight.js";
import { useCalendarInheritedTask, useCalendarProgress, useCalendarRenderIndex } from "./CalendarRenderIndex.js";
import { usePastEventFlag } from "./usePastEventFlag.js";
import { isMobileUx } from "../../lib/useMobileUx.js";
import { suppressMobileContextMenu } from "../../lib/mobile-context-menu.js";
import { mobileBlockStyle } from "../mobile/mobile-block-style.js";
import { useTasksStore, useTouchMoveStore } from "../../stores/index.js";
import { useMatrixPriorityColor } from "../priority-matrix/useEffectiveMatrixDisplay.js";
import { occurrenceRefOf } from "../../lib/virtual-id.js";

interface Props {
  task: Task;
  isFirst: boolean;
  isLast: boolean;
  /** The day this segment renders (multi-day spans slice ONE shared background
   *  gradient across their segments instead of restarting it per day). */
  date?: Date;
  /** Negative overlap (px) for non-leading/trailing segments so adjacent cells
   *  visually connect. Month grid uses 7, the week all-day row uses 3. */
  overlapPx: number;
  onClick: () => void;
  onMoveStart?: (e: MouseEvent) => void;
  onResizeLeft?: (e: MouseEvent) => void;
  onResizeRight?: (e: MouseEvent) => void;
  /** Per-view rule for whether each edge can be dragged to resize. */
  canResizeLeft?: boolean;
  canResizeRight?: boolean;
  /** Right margin left on a single-day chip so the column keeps a clickable
   *  strip for create-on-empty (mirrors the timed grid's ~5% gap). The week/day
   *  all-day row passes this; the month grid omits it (cells are click-anywhere). */
  rightGap?: number | string;
  /** Tighter chip for phone month cells. */
  compact?: boolean;
}

/**
 * One horizontal segment of an all-day task. Shared by the month grid and the
 * week/day all-day row — the views differ only in overlap width and which edges
 * are resizable, both passed as props.
 */
export function AllDayChip({
  task, isFirst, isLast, date, overlapPx, onClick,
  onMoveStart, onResizeLeft, onResizeRight, canResizeLeft, canResizeRight, rightGap, compact,
}: Props) {
  const index = useCalendarRenderIndex();
  const inheritedTask = useCalendarInheritedTask(task);
  const done = task.status === "done";
  const pastEvent = usePastEventFlag(task);
  const complete = useTasksStore((s) => s.complete);
  const completeOccurrence = useTasksStore((s) => s.completeOccurrence);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);
  const [dragSource, setDragSource] = useState(false);
  const touchMoving = useTouchMoveStore((s) => s.movingId === task.id);
  const { ref: flashRef, flash } = useFlashHighlight(task);

  const progress = useCalendarProgress(task.id);

  function toggleDone(e: MouseEvent) {
    e.stopPropagation();
    if (task.virtual && task.parent_id) {
      void completeOccurrence(task.parent_id, occurrenceRefOf(task), !done);
    } else {
      void complete(task.id, !done);
    }
  }

  const contextColor = index.contextColorOf(task);
  const { tint, gradient } = index.blockStyle;
  // Matrix scope: continuous diagonal color for the priority accent/fill
  // (placed-only — see useMatrixPriorityColor).
  const priorityColor = useMatrixPriorityColor(inheritedTask);
  const { vars: colorStyle, washed: hasColor } = washStyle(inheritedTask, contextColor, tint, gradient, priorityColor);
  // Multi-day spans render per-day segments — stretch the background to the
  // whole span's width and show each segment its slice, so the gradient runs
  // CONTINUOUSLY across the bar instead of restarting every day. (position-x %
  // is relative to `container − image` width, hence the /(count−1).)
  const count = spanDays(task);
  const segIdx = date && task.due_at
    ? Math.min(count - 1, Math.max(0, Math.round((dayStart(date).getTime() - dayStart(new Date(task.due_at)).getTime()) / 86_400_000)))
    : 0;
  const slice: CSSProperties = count > 1
    ? { backgroundSize: `${count * 100}% 100%`, backgroundPosition: `${(segIdx / (count - 1)) * 100}% 0%` }
    : {};
  const mob = isMobileUx()
    ? mobileBlockStyle({
        priority: task.priority,
        matrixColor: priorityColor,
        customColor: inheritedTask.custom_color,
        topicColors: index.topicColorsOf(task),
        calendarColor: index.calendarColorOf(task),
        isEvent: task.is_event,
      })
    : null;
  // Every trailing segment (single-day OR the last day of a multi-day span)
  // leaves a right gap so all chips end with a consistent margin and there's
  // space to click-create; interior segments connect via the negative overlap.
  const margins = {
    marginLeft: isFirst ? undefined : -overlapPx,
    marginRight: isLast ? rightGap : -overlapPx,
  };
  // Mobile: a solid custom/neutral body (no per-segment slice) + priority
  // border/glow; desktop keeps the continuous washed gradient across segments.
  const style: CSSProperties = mob ? { ...mob.container, ...margins } : { ...colorStyle, ...slice, ...margins };

  return (
    <>
      <div
        ref={flashRef}
        data-chip
        data-task-id={isFirst ? task.id : undefined}
        data-touch-task-id={task.id}
        style={style}
        // `isFirst` carries desktop HTML5 DnD; disabled on mobile so the Android
        // WebView can't start a native drag (→ Samsung "open in split window" at
        // the screen edge). Mobile moves run through the touch layer.
        draggable={isFirst && !mob}
        onDragStart={(e) => {
          if (!isFirst) { e.preventDefault(); return; }
          setDragTaskId(e, task.id);
          setTaskDragging(true);
          beginBlockDrag(e, 0, 60, {
            task,
            vars: colorStyle,
            washed: hasColor,
            toneClass: priorityToneClass(task.priority, "block"),
          });
          setTimeout(() => setDragSource(true), 0);
        }}
        onDragEnd={() => { setTaskDragging(false); endBlockDrag(); setDragSource(false); }}
        onMouseDown={(e) => {
          if (mob) return;
          if ((e.target as HTMLElement).closest("button")) return;
          onMoveStart?.(e);
        }}
        className={cn(
          "relative flex cursor-pointer items-center overflow-hidden font-medium hover:opacity-90",
          compact ? "h-4 text-[calc(10px*var(--app-text-scale))]" : "h-5 text-[calc(11px*var(--app-text-scale))]",
          mob && "border bg-card",
          !mob && hasColor && "task-wash",
          !mob && !hasColor && priorityToneClass(task.priority, "allday"),
          isFirst ? (compact ? "rounded-l pl-1" : "rounded-l pl-1.5") : "rounded-l-none pl-0",
          isLast ? (compact ? "rounded-r pr-0.5" : "rounded-r pr-1") : "rounded-r-none pr-0",
          done && "opacity-50 line-through",
          (dragSource || touchMoving) && "opacity-30",
          flash && "flash-highlight",
        )}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        onContextMenu={(e) => {
          if (suppressMobileContextMenu(e)) return;
          e.preventDefault();
          e.stopPropagation();
          setCtxMenu({ x: e.clientX, y: e.clientY });
        }}
      >
        {isFirst && canResizeLeft && onResizeLeft && (
          <div className="absolute left-0 top-0 z-10 h-full w-2 cursor-col-resize hover:bg-black/20"
            onMouseDown={(e) => { e.stopPropagation(); onResizeLeft(e); }} />
        )}
        <div className={cn("flex flex-1 items-center truncate", compact ? "gap-0.5 px-0.5" : "gap-1 px-1")}>
          {isFirst && !task.is_event && (
            <button
              type="button"
              onClick={toggleDone}
              onMouseDown={(e) => e.stopPropagation()}
              draggable={false}
              aria-label={done ? "Mark incomplete" : "Mark complete"}
              className={cn(
                "group/btn inline-flex shrink-0 items-center justify-center rounded-full border-[1.5px] border-current transition-colors",
                compact ? "h-2.5 w-2.5" : "h-3 w-3",
                done ? "bg-current/25" : "bg-transparent hover:bg-current/20",
              )}
            >
              <Check className={cn(compact ? "h-1.5 w-1.5" : "h-2 w-2", "transition-opacity duration-150", done ? "opacity-100" : "opacity-0 group-hover/btn:opacity-40")} />
            </button>
          )}
          {isFirst ? (
            <span className="truncate" style={mob ? mob.titleText : undefined}>{task.title}</span>
          ) : null}
          {isFirst && progress.total > 0 && (
            <span className={cn("shrink-0 rounded bg-black/10 px-1 tabular-nums dark:bg-white/15", compact ? "text-[calc(8px*var(--app-text-scale))]" : "text-[calc(9px*var(--app-text-scale))]")}>
              {progress.done}/{progress.total}
            </span>
          )}
        </div>
        {isLast && task.recurrence_id && (
          // Series marker at the span's right end, inked in a near-shade of the
          // chip's own fill (`--c-ink`, block-wash).
          <Repeat
            aria-hidden
            className={cn("shrink-0", compact ? "mr-0.5 h-2 w-2" : "mr-1 h-2.5 w-2.5")}
            style={{ color: "var(--c-ink, currentColor)" }}
          />
        )}
        {isLast && canResizeRight && onResizeRight && (
          <div className="absolute right-0 top-0 z-10 h-full w-2 cursor-col-resize hover:bg-black/20"
            onMouseDown={(e) => { e.stopPropagation(); onResizeRight(e); }} />
        )}
        {!isLast && <span className="mr-0.5 shrink-0 text-[calc(9px*var(--app-text-scale))] opacity-60">›</span>}
        {!done && pastEvent && <FaintOverlay />}
      </div>
      {ctxMenu && (
        <TaskContextMenu task={task} x={ctxMenu.x} y={ctxMenu.y} onClose={() => setCtxMenu(null)} />
      )}
    </>
  );
}
